import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, FileText, AlertTriangle, DollarSign, 
  Percent, Calendar, Building2, X, Check, ChevronDown, ChevronUp,
  ArrowRightLeft, RefreshCw, Info
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const PAYMENT_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'bi-weekly', label: 'Bi-Weekly' },
  { value: 'accelerated-bi-weekly', label: 'Accelerated Bi-Weekly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'accelerated-weekly', label: 'Accelerated Weekly' }
];

const TERM_OPTIONS = [
  { value: 1, label: '1 Year' },
  { value: 2, label: '2 Years' },
  { value: 3, label: '3 Years' },
  { value: 4, label: '4 Years' },
  { value: 5, label: '5 Years' },
  { value: 7, label: '7 Years' },
  { value: 10, label: '10 Years' }
];

const emptyOffer = {
  lender: '',
  rate: '',
  term: 5,
  rate_type: 'fixed',
  payment_frequency: 'monthly',
  transfer_type: 'straight_transfer',
  cash_incentive: '',
  transfer_fees: '',
  notes: ''
};

const OffersManager = ({ mortgage }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [formData, setFormData] = useState(emptyOffer);
  const [saving, setSaving] = useState(false);
  const [expandedOffer, setExpandedOffer] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  useEffect(() => {
    loadOffers();
  }, []);
  
  const loadOffers = async () => {
    try {
      const response = await api.get('/api/offers');
      setOffers(response.data || []);
    } catch (error) {
      console.error('Error loading offers:', error);
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.lender || !formData.rate) {
      toast.error('Please fill in lender name and interest rate');
      return;
    }
    
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        rate: parseFloat(formData.rate),
        term: parseInt(formData.term),
        cash_incentive: parseFloat(formData.cash_incentive) || 0,
        transfer_fees: parseFloat(formData.transfer_fees) || 0
      };
      
      if (editingOffer) {
        await api.put(`/api/offers/${editingOffer.offer_id}`, payload);
        toast.success('Offer updated successfully');
      } else {
        await api.post('/api/offers', payload);
        toast.success('Offer added successfully');
      }
      
      setFormData(emptyOffer);
      setShowForm(false);
      setEditingOffer(null);
      loadOffers();
    } catch (error) {
      console.error('Error saving offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to save offer');
    } finally {
      setSaving(false);
    }
  };
  
  const handleEdit = (offer) => {
    setFormData({
      lender: offer.lender,
      rate: offer.rate,
      term: offer.term,
      rate_type: offer.rate_type || 'fixed',
      payment_frequency: offer.payment_frequency || 'monthly',
      transfer_type: offer.transfer_type || 'straight_transfer',
      cash_incentive: offer.cash_incentive || '',
      transfer_fees: offer.transfer_fees || '',
      notes: offer.notes || ''
    });
    setEditingOffer(offer);
    setShowForm(true);
  };
  
  const handleDelete = async (offerId) => {
    try {
      await api.delete(`/api/offers/${offerId}`);
      toast.success('Offer deleted');
      setDeleteConfirm(null);
      loadOffers();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Failed to delete offer');
    }
  };
  
  const handleCancel = () => {
    setFormData(emptyOffer);
    setShowForm(false);
    setEditingOffer(null);
  };
  
  const calculateMonthlyPayment = (principal, rate, years) => {
    if (!principal || !rate || !years) return 0;
    const monthlyRate = rate / 100 / 12;
    const numPayments = years * 12;
    return (principal * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
           (Math.pow(1 + monthlyRate, numPayments) - 1);
  };
  
  const getBestOffer = () => {
    if (offers.length === 0) return null;
    return offers.reduce((best, current) => {
      const bestEffectiveRate = best.rate - (best.cash_incentive || 0) / 10000;
      const currentEffectiveRate = current.rate - (current.cash_incentive || 0) / 10000;
      return currentEffectiveRate < bestEffectiveRate ? current : best;
    });
  };
  
  const bestOffer = getBestOffer();
  
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div data-testid="offers-manager" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-heading font-semibold flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" />
            Bank Offers
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Track and compare mortgage offers you've received from lenders
          </p>
        </div>
        
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-offer-btn"
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            <Plus className="h-4 w-4" />
            Add Offer
          </button>
        )}
      </div>
      
      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-card border border-primary/30 rounded-xl p-6">
          <h4 className="text-lg font-semibold mb-4">
            {editingOffer ? 'Edit Offer' : 'Add New Offer'}
          </h4>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Lender Name */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Lender Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="lender"
                  value={formData.lender}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., TD Bank, RBC, Scotiabank"
                  required
                />
              </div>
              
              {/* Interest Rate */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Interest Rate (%) <span className="text-red-400">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="rate"
                  value={formData.rate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="4.89"
                  required
                />
              </div>
              
              {/* Rate Type */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Rate Type</label>
                <select
                  name="rate_type"
                  value={formData.rate_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="fixed">Fixed</option>
                  <option value="variable">Variable</option>
                </select>
              </div>
              
              {/* Term Duration */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Term Duration</label>
                <select
                  name="term"
                  value={formData.term}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {TERM_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Payment Frequency */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Payment Frequency</label>
                <select
                  name="payment_frequency"
                  value={formData.payment_frequency}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {PAYMENT_FREQUENCIES.map(freq => (
                    <option key={freq.value} value={freq.value}>{freq.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Transfer Type */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Transfer Type</label>
                <select
                  name="transfer_type"
                  value={formData.transfer_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="straight_transfer">Straight Transfer (Switch)</option>
                  <option value="refinance">Refinance</option>
                </select>
              </div>
              
              {/* Cash Incentive */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Cash Incentive ($)</label>
                <input
                  type="number"
                  step="0.01"
                  name="cash_incentive"
                  value={formData.cash_incentive}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="2500"
                />
              </div>
              
              {/* Transfer Fees */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Transfer/Legal Fees ($)</label>
                <input
                  type="number"
                  step="0.01"
                  name="transfer_fees"
                  value={formData.transfer_fees}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="0"
                />
              </div>
              
              {/* Notes */}
              <div className="lg:col-span-3">
                <label className="block text-sm font-medium mb-1.5">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Any additional terms, conditions, or notes about this offer..."
                />
              </div>
            </div>
            
            {/* Refinance Warning */}
            {formData.transfer_type === 'refinance' && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-6 w-6 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="font-semibold text-yellow-400 mb-1">Refinancing Requires Stress Test</h5>
                    <p className="text-sm text-muted-foreground mb-2">
                      As of Canada's 2024 mortgage rules, if you choose to <strong>refinance</strong> your mortgage 
                      (change the terms, increase your mortgage amount, or access equity), you will be required 
                      to pass the <strong>mortgage stress test</strong>.
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li className="flex items-start gap-2">
                        <span className="text-yellow-400">•</span>
                        The stress test qualifies you at the higher of: 5.25% or your contract rate + 2%
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-yellow-400">•</span>
                        There is a risk of being denied if your financial situation has changed
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-yellow-400">•</span>
                        Additional legal and appraisal fees may apply
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {/* Straight Transfer Info */}
            {formData.transfer_type === 'straight_transfer' && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Check className="h-6 w-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="font-semibold text-green-400 mb-1">Straight Transfer - No Stress Test Required</h5>
                    <p className="text-sm text-muted-foreground">
                      With a <strong>straight transfer</strong> (also called a switch), you simply move your 
                      mortgage to a new lender at renewal without changing the terms or amount. As of 
                      Canada's 2024 mortgage rules, this <strong>no longer requires a stress test</strong>, 
                      making it easier to switch lenders for better rates.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Form Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    {editingOffer ? 'Update Offer' : 'Save Offer'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Offers List */}
      {offers.length === 0 && !showForm ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-semibold mb-2">No Offers Yet</h4>
          <p className="text-muted-foreground text-sm mb-4">
            Start tracking bank offers you receive during your mortgage renewal process.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Your First Offer
          </button>
        </div>
      ) : offers.length > 0 && (
        <div className="space-y-4">
          {/* Best Offer Highlight */}
          {offers.length > 1 && bestOffer && (
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Check className="h-5 w-5 text-primary" />
                <span className="font-semibold">Best Offer</span>
              </div>
              <p className="text-sm text-muted-foreground">
                <strong>{bestOffer.lender}</strong> at <strong>{bestOffer.rate}%</strong> {bestOffer.rate_type}
                {bestOffer.cash_incentive > 0 && ` with $${bestOffer.cash_incentive.toLocaleString()} cash back`}
              </p>
            </div>
          )}
          
          {/* Offers Grid */}
          <div className="grid grid-cols-1 gap-4">
            {offers.map((offer) => {
              const isBest = bestOffer?.offer_id === offer.offer_id && offers.length > 1;
              const isExpanded = expandedOffer === offer.offer_id;
              const monthlyPayment = mortgage?.current_balance 
                ? calculateMonthlyPayment(mortgage.current_balance, offer.rate, mortgage.amortization_remain || 20)
                : null;
              
              return (
                <div
                  key={offer.offer_id}
                  data-testid={`offer-${offer.offer_id}`}
                  className={`bg-card border rounded-xl overflow-hidden transition-all ${
                    isBest ? 'border-primary ring-2 ring-primary/20' : 'border-border'
                  }`}
                >
                  {/* Offer Header */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => setExpandedOffer(isExpanded ? null : offer.offer_id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-lg">{offer.lender}</h4>
                            {isBest && (
                              <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                                Best Rate
                              </span>
                            )}
                            {offer.transfer_type === 'refinance' && (
                              <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Refinance
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Percent className="h-3 w-3" />
                              {offer.rate}% {offer.rate_type}
                            </span>
                            <span>•</span>
                            <span>{offer.term} year term</span>
                            {offer.cash_incentive > 0 && (
                              <>
                                <span>•</span>
                                <span className="text-green-400">${offer.cash_incentive.toLocaleString()} cash back</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {monthlyPayment && (
                          <div className="text-right hidden sm:block">
                            <p className="text-xs text-muted-foreground">Est. Monthly</p>
                            <p className="font-mono font-semibold">${monthlyPayment.toLocaleString('en-CA', { maximumFractionDigits: 0 })}</p>
                          </div>
                        )}
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-border p-4 bg-background/50 space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">Interest Rate</p>
                          <p className="font-semibold">{offer.rate}%</p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">Rate Type</p>
                          <p className="font-semibold capitalize">{offer.rate_type}</p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">Term</p>
                          <p className="font-semibold">{offer.term} Year{offer.term > 1 ? 's' : ''}</p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">Payment Frequency</p>
                          <p className="font-semibold capitalize">{offer.payment_frequency?.replace('-', ' ')}</p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">Transfer Type</p>
                          <p className={`font-semibold ${offer.transfer_type === 'refinance' ? 'text-yellow-400' : 'text-green-400'}`}>
                            {offer.transfer_type === 'straight_transfer' ? 'Straight Transfer' : 'Refinance'}
                          </p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">Cash Incentive</p>
                          <p className="font-semibold text-green-400">
                            ${(offer.cash_incentive || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">Transfer Fees</p>
                          <p className="font-semibold">
                            ${(offer.transfer_fees || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">Added On</p>
                          <p className="font-semibold">{offer.added_date}</p>
                        </div>
                      </div>
                      
                      {/* Transfer Type Warning/Info */}
                      {offer.transfer_type === 'refinance' && (
                        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-muted-foreground">
                            <strong className="text-yellow-400">Stress test required.</strong> You'll need to qualify 
                            at the higher of 5.25% or your rate + 2%.
                          </p>
                        </div>
                      )}
                      
                      {offer.transfer_type === 'straight_transfer' && (
                        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-muted-foreground">
                            <strong className="text-green-400">No stress test required.</strong> Straight transfers 
                            at renewal are exempt from the mortgage stress test.
                          </p>
                        </div>
                      )}
                      
                      {offer.notes && (
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">Notes</p>
                          <p className="text-sm">{offer.notes}</p>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(offer);
                          }}
                          className="flex items-center gap-2 px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-colors"
                        >
                          <Edit2 className="h-4 w-4" />
                          Edit
                        </button>
                        
                        {deleteConfirm === offer.offer_id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Delete?</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(offer.offer_id);
                              }}
                              className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 transition-colors"
                            >
                              Yes
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(null);
                              }}
                              className="px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-colors"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm(offer.offer_id);
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg text-sm transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Info Section */}
      <div className="bg-muted/30 border border-border rounded-xl p-4">
        <h4 className="font-semibold mb-2 flex items-center gap-2">
          <Info className="h-4 w-4 text-primary" />
          Canadian Mortgage Renewal Rules (2024)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h5 className="font-medium text-green-400 mb-1">Straight Transfer (Switch)</h5>
            <ul className="text-muted-foreground space-y-1">
              <li>• No stress test required</li>
              <li>• Simply move your existing mortgage to a new lender</li>
              <li>• Cannot change mortgage amount or terms significantly</li>
              <li>• Usually no appraisal needed</li>
              <li>• Minimal legal fees (often covered by new lender)</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-yellow-400 mb-1">Refinance</h5>
            <ul className="text-muted-foreground space-y-1">
              <li>• Stress test required (qualify at 5.25% or rate+2%)</li>
              <li>• Allows you to access equity or change terms</li>
              <li>• New appraisal typically required</li>
              <li>• Legal fees apply (~$1,000-$2,500)</li>
              <li>• Risk of denial if income/debt ratio changed</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OffersManager;
